/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useSocketIoClient } from "@/hooks/transcriber/useSocketIoClient";
import { useTranscriptionStreamingStore } from "@/store/transcriptionStreaming.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { useAuthStore } from "@/store/auth.store";
import {
  TranscriberClientEvents,
  TranscriberServerEvents,
} from "@/hooks/transcriber/constants";
import type {
  TranscriptCompletedMessage,
  TranscriptEndMessage,
} from "@/hooks/transcriber/types";

interface UseSyncRecordingTranscription {
  roomId: string;
}

export const useSyncRecordingTranscription = ({
  roomId,
}: UseSyncRecordingTranscription): {
  transcription: TranscriptEndMessage | null;
  finishSession: () => void;
  finished: boolean;
  reset: () => void;
  isProviderReady: boolean;
} => {
  const { socket } = useSocketIoClient();
  const [transcription, setTranscription] =
    useState<TranscriptEndMessage | null>(null);
  const [finished, setFinished] = useState(false);
  const { setStreamText, resetStreamText, finishSteamText } =
    useTranscriptionStreamingStore.getState();
  const {
    inputLanguage,
    noiseReduction,
    transcriptionProvider,
    noiseSuppression,
    diarize,
  } = useRecordingSettingsStore.getState();
  const { userData } = useAuthStore.getState();
  const [isProviderReady, setIsProviderReady] = useState(false);

  const reset = (): void => {
    setFinished(false);
    resetStreamText();
  };

  const finishSession = (): void => {
    resetStreamText();
  };

  const startSession = (): void => {
    socket.emit(TranscriberClientEvents.AUDIO_START, {
      inputLanguage: inputLanguage,
      noiseReduction: noiseReduction,
      userEmail: userData?.email,
      userId: userData?.id,
      transcriptionProvider: transcriptionProvider,
      noiseSuppression: noiseSuppression,
      diarize: diarize,
      roomId: roomId,
    });
  };

  const closeSession = (): void => {
    socket.close();
  };

  useEffect(() => {
    startSession();
    return (): void => {
      closeSession();
    };
  }, []);

  const handlers = useMemo(() => {
    return {
      [TranscriberServerEvents.PROVIDER_READY]: (): void => {
        setIsProviderReady(true);
      },
      [TranscriberServerEvents.PROVIDER_WAIT]: (): void => {
        setIsProviderReady(false);
      },
      [TranscriberServerEvents.TRANSCRIPT_PARTIAL]: (): void => {},
      [TranscriberServerEvents.TRANSCRIPT_COMPLETED]: (
        data: TranscriptCompletedMessage
      ): void => {
        setStreamText({
          text: data.transcription,
          startTime: data.startTime,
        });
      },
      [TranscriberServerEvents.TRANSCRIPT_END]: (
        data: TranscriptEndMessage
      ): void => {
        finishSteamText();
        setTranscription(data);
        setFinished(true);
      },
    };
  }, [finishSteamText, setFinished, setStreamText]);

  useEffect(() => {
    Object.entries(handlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });
    return (): void => {
      Object.entries(handlers).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
    };
  }, [socket, handlers]);

  return {
    transcription: transcription,
    finishSession,
    finished,
    reset,
    isProviderReady,
  };
};
